/**
 * Appointment Date of Birth Component
 * Handles date of birth input using Cleave.js with validation
 *
 * IMPORTANT: When modifying this file, update APPOINTMENT_SCHEDULING_FEATURE.md
 */

// Initialize date of birth input with Cleave.js
function initializeDateOfBirthInput() {
    const dobInput = document.getElementById('date-of-birth-input');

    if (!dobInput) {
        console.warn('Date of birth input element not found');
        return null;
    }

    // Initialize Cleave.js for date formatting
    const cleave = new Cleave('#date-of-birth-input', {
        date: true,
        datePattern: ['m', 'd', 'Y'],
        delimiter: '/',
        blocks: [2, 2, 4],
        numericOnly: true
    });

    // Add validation and age restrictions
    setupDateOfBirthValidation(dobInput, cleave);

    return cleave;
}

// Setup validation for date of birth input with Cleave.js integration
function setupDateOfBirthValidation(input, cleave) {
    // Minimum age (18 years old)
    const minAge = 18;
    const maxAge = 120;

    // Get current date
    const today = new Date();
    const minDate = new Date(today.getFullYear() - maxAge, today.getMonth(), today.getDate());
    const maxDate = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());

    // Add event listeners for validation
    input.addEventListener('blur', function() {
        validateDateOfBirth(input, minDate, maxDate, minAge);
    });

    // Real-time validation as user types (with Cleave.js integration)
    input.addEventListener('input', function() {
        // Clear previous error states on input
        clearDateOfBirthError(input);

        // Check if date is complete and valid (MM/DD/YYYY = 10 characters)
        const value = input.value.trim();
        if (value.length === 10 && value.includes('/')) {
            // Use a small delay to let Cleave.js finish formatting
            setTimeout(() => {
                validateDateOfBirth(input, minDate, maxDate, minAge);
            }, 50);
        }
    });

    // Let Cleave.js handle character validation and formatting

    // Let Cleave.js handle formatting - no interference needed
}

// Validate date of birth
function validateDateOfBirth(input, minDate, maxDate, minAge) {
    const value = input.value.trim();

    if (!value) {
        return { isValid: false, message: 'Date of birth is required' };
    }

    // Check if date format is complete (MM/DD/YYYY)
    const dateParts = value.split('/');
    if (dateParts.length !== 3 || dateParts.some(part => !part)) {
        showDateOfBirthError(input, 'Please enter a complete date (MM/DD/YYYY)');
        return { isValid: false, message: 'Incomplete date format' };
    }

    // Parse the date
    const month = parseInt(dateParts[0], 10);
    const day = parseInt(dateParts[1], 10);
    const year = parseInt(dateParts[2], 10);

    // Validate month
    if (month < 1 || month > 12) {
        showDateOfBirthError(input, 'Please enter a valid month (01-12)');
        return { isValid: false, message: 'Invalid month' };
    }

    // Validate day
    if (day < 1 || day > 31) {
        showDateOfBirthError(input, 'Please enter a valid day (01-31)');
        return { isValid: false, message: 'Invalid day' };
    }

    // Validate year
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) {
        showDateOfBirthError(input, 'Please enter a valid year');
        return { isValid: false, message: 'Invalid year' };
    }

    // Create date object and validate it's a real date
    const birthDate = new Date(year, month - 1, day);
    if (birthDate.getDate() !== day || birthDate.getMonth() !== month - 1 || birthDate.getFullYear() !== year) {
        showDateOfBirthError(input, 'Please enter a valid date');
        return { isValid: false, message: 'Invalid date' };
    }

    // Check age restrictions
    const age = calculateAge(birthDate);
    if (age < minAge) {
        const yearsShort = minAge - age;
        let errorMessage;
        if (age < 0) {
            errorMessage = 'Please enter a valid date of birth';
        } else if (yearsShort === 1) {
            errorMessage = `You must be at least ${minAge} years old to book an appointment.`;
        } else {
            errorMessage = `You must be at least 18 years old to book an appointment.`;
        }
        showDateOfBirthError(input, errorMessage);
        return { isValid: false, message: 'Under minimum age', age: age };
    }
    // Fun Fact: The oldest living American today is Naomi Whitehead, who turned 114 years old on September 26, 2024
    if (age > 125) {
        showDateOfBirthError(input, 'Please enter a valid birth year');
        return { isValid: false, message: 'Age too high', age: age };
    }

    // Clear any errors if validation passes
    clearDateOfBirthError(input);
    return { isValid: true, age: age, birthDate: birthDate };
}

// Calculate age from birth date
function calculateAge(birthDate) {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
}

// Show date of birth error
function showDateOfBirthError(input, message) {
    // Remove existing error classes
    input.classList.remove('valid');
    input.classList.add('error');

    // Show error message
    let errorElement = input.parentNode.querySelector('.dob-error-message');
    if (!errorElement) {
        errorElement = document.createElement('div');
        errorElement.className = 'dob-error-message';
        input.parentNode.appendChild(errorElement);
    }
    errorElement.textContent = message;
    errorElement.style.display = 'block';
}

// Clear date of birth error
function clearDateOfBirthError(input) {
    input.classList.remove('error');
    input.classList.add('valid');

    const errorElement = input.parentNode.querySelector('.dob-error-message');
    if (errorElement) {
        errorElement.style.display = 'none';
    }
}

// Save date of birth to session storage
function saveDateOfBirthToSession(dateOfBirth) {
    return saveToSession('selectedDateOfBirth', dateOfBirth, 'dobTimestamp');
}

// Load date of birth from session storage
function loadDateOfBirthFromSession() {
    return loadFromSession('selectedDateOfBirth', 'dobTimestamp');
}

// Clear date of birth from session storage
function clearDateOfBirthFromSession() {
    return clearFromSession('selectedDateOfBirth', 'dobTimestamp');
}

// Show date of birth selection step
function showDateOfBirthStep() {
    const dobContainer = document.querySelector('.date-of-birth-container');
    if (dobContainer) {
        dobContainer.classList.add('active');
        dobContainer.style.display = 'block';
    }
}

// Hide date of birth selection step
function hideDateOfBirthStep() {
    const dobContainer = document.querySelector('.date-of-birth-container');
    if (dobContainer) {
        dobContainer.classList.remove('active');
        dobContainer.style.display = 'none';
    }
}

// Setup date of birth step functionality
function setupDateOfBirthStep() {
    const continueBtn = document.getElementById('dob-continue-btn');
    const backBtn = document.getElementById('dob-back-btn');

    if (continueBtn) {
        continueBtn.addEventListener('click', function(e) {
            e.preventDefault();
            proceedFromDateOfBirth();
        });
    }

    if (backBtn) {
        backBtn.addEventListener('click', function(e) {
            e.preventDefault();
            goBackFromDateOfBirth();
        });
    }

    // Handle Enter key on date input
    const dobInput = document.getElementById('date-of-birth-input');
    if (dobInput) {
        dobInput.addEventListener('keypress', function(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                proceedFromDateOfBirth();
            }
        });
    }
}

// Proceed from date of birth step
function proceedFromDateOfBirth() {
    const dobInput = document.getElementById('date-of-birth-input');
    if (!dobInput) {
        console.error('Date of birth input not found');
        return;
    }

    // Validate the date of birth
    const validation = validateDateOfBirth(dobInput);

    if (validation.isValid) {
        // Double-check age requirement before proceeding
        if (validation.age < 18) {
            showDateOfBirthError(dobInput, 'You must be at least 18 years old to book an appointment');
            return;
        }

        // Save to session storage
        saveDateOfBirthToSession(dobInput.value);

        // Hide date of birth step
        hideDateOfBirthStep();

        // Show package selection step
        if (typeof showPackageSelection === 'function') {
            showPackageSelection();
        }
    }
    // If validation fails, error is already shown by validateDateOfBirth
}

// Go back from date of birth step
function goBackFromDateOfBirth() {
    // Hide date of birth step
    hideDateOfBirthStep();

    // Show gender selection step
    if (typeof showGenderPreSelection === 'function') {
        showGenderPreSelection();
    }
}

// Initialize date of birth functionality
function initializeDateOfBirth() {
    // Initialize the input with Cleave.js
    initializeDateOfBirthInput();

    // Setup event handlers
    setupDateOfBirthStep();
}

// Expose functions globally
window.DateOfBirth = {
    initializeDateOfBirth,
    showDateOfBirthStep,
    hideDateOfBirthStep,
    saveDateOfBirthToSession,
    loadDateOfBirthFromSession,
    clearDateOfBirthFromSession,
    validateDateOfBirth,
    proceedFromDateOfBirth,
    goBackFromDateOfBirth
};

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    initializeDateOfBirth();
});
